import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { getFeaturedArticles } from '@/mocks/articles';

export default function HomePage() {
  const { t } = useTranslation();
  const featuredArticles = getFeaturedArticles();

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '麻豆传媒app官网',
    url: 'https://madouapp.com/',
    description: '麻豆传媒app官方下载与使用指南，提供下载方式、安装教程、使用指南及常见问题解答。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://madouapp.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '麻豆传媒',
    url: 'https://madouapp.com/',
    description: '麻豆传媒是一家专注于移动互联网内容服务的创新型科技企业。',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20soft%20flowing%20shapes%20in%20coral%20and%20cream%20tones%2C%20minimalist%20elegant%20design%2C%20smooth%20curves%2C%20modern%20digital%20atmosphere%2C%20subtle%20geometric%20patterns%2C%20artistic%20composition%2C%20premium%20quality&width=1920&height=1080&seq=hero-bg&orientation=landscape"
            alt="麻豆传媒app"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-heading leading-tight">
            {t('hero_title')}
          </h1>
          <p className="text-base md:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            {t('hero_subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/category/download-guide"
              className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-base font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 transform"
            >
              {t('hero_cta')}
            </Link>
            <Link
              to="/about"
              className="px-8 py-3 bg-white/15 text-white backdrop-blur-sm rounded-full text-base font-medium hover:bg-white/25 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('hero_secondary')}
            </Link>
          </div>
        </div>
      </section>

      {/* Category Section */}
      <section className="py-14 md:py-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
              {t('category_title')}
            </h2>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
              {t('category_subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group relative bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer hover:scale-[1.02]"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center mb-2">
                    <i className={`${cat.icon} text-white text-sm`}></i>
                  </div>
                  <h3 className="text-white font-semibold text-sm md:text-base font-heading">
                    {cat.name}
                  </h3>
                  <p className="text-white/70 text-xs mt-1 line-clamp-2">
                    {cat.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
              {t('featured_articles')}
            </h2>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
              {t('featured_subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {featuredArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                      {categories.find((c) => c.slug === article.category)?.name}
                    </span>
                    <span className="text-xs text-foreground-400">{article.date}</span>
                  </div>
                  <h3 className="text-foreground-900 font-semibold text-sm md:text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors font-heading">
                    {article.title}
                  </h3>
                  <p className="text-foreground-500 text-xs md:text-sm line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {article.views > 10000
                        ? `${(article.views / 10000).toFixed(1)}万`
                        : article.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {article.date}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 md:py-24 px-4 md:px-6">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20abstract%20background%20with%20soft%20flowing%20shapes%20in%20cream%20and%20coral%20gradients%2C%20elegant%20smooth%20curves%2C%20modern%20digital%20atmosphere%2C%20premium%20quality%2C%20artistic%20composition&width=1920&height=600&seq=cta-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/35"></div>
        </div>
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white font-heading mb-4">
            {t('download_cta_title')}
          </h2>
          <p className="text-white/80 text-sm md:text-base mb-8">
            {t('download_cta_subtitle')}
          </p>
          <Link
            to="/category/download-guide"
            className="inline-flex px-8 py-3.5 bg-accent-500 text-background-50 rounded-full text-base font-semibold hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105 transform"
          >
            {t('download_cta_button')}
          </Link>
        </div>
      </section>
    </>
  );
}